// Google Analytics 4 (G-S6JSK59W1W) + Meta Pixel (1155744453082317) Event & Conversion Tracking
// Dönüşüm API (CAPI) uyumlu gelişmiş eşleştirme + enhanced conversions
// KVKK: Meta, OpenAI ve kullanıcı verisi (gelişmiş eşleştirme) yalnızca pazarlama izniyle gönderilir;
// GA4 yalnızca analitik izniyle çalışır: izin yoksa GA4 betiği hiç yüklenmez (client/index.html), trackGA4Event hiçbir
// şey göndermez ve Google'a istek gitmez. Google İzin Modu varsayılanı "denied"; izinsiz çerezsiz ping gönderen
// "gelişmiş mod" kullanılmaz. İzinden bağımsız, çerez ve cihaz depolaması kullanmayan toplu sayım: ./olcum.ts.
// Bkz. ./cerez_izni.ts
import { analitikIzniVar, pazarlamaIzniVar } from "./cerez_izni";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    oaiq?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// ── GA4 Measurement ID ──
const GA4_ID = 'G-S6JSK59W1W';

// ── Event Types ──
type EventName =
  | 'generate_lead'
  | 'schedule_meeting'
  | 'request_callback'
  | 'submit_feedback'
  | 'calculate_roi'
  | 'page_view'
  | 'contact'
  | 'begin_checkout'
  | 'view_item';

interface EventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  currency?: string;
  content_name?: string;
  content_category?: string;
  [key: string]: any;
}

// ── User Data for Enhanced Conversions / Advanced Matching ──
interface UserData {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
}

/**
 * GA4 event gönderimi
 */
export function trackGA4Event(eventName: string, params?: EventParams) {
  try {
    if (window.gtag && analitikIzniVar()) {
      window.gtag('event', eventName, {
        send_to: GA4_ID,
        ...params,
      });
    }
  } catch (e) {
    console.warn('[Analytics] GA4 event failed:', e);
  }
}

/**
 * Meta Pixel event gönderimi
 */
export function trackMetaEvent(eventName: string, params?: EventParams) {
  try {
    if (window.fbq && pazarlamaIzniVar()) {
      window.fbq('track', eventName, params);
    }
  } catch (e) {
    console.warn('[Analytics] Meta Pixel event failed:', e);
  }
}

/**
 * Meta Pixel - Gelişmiş Eşleştirme (Advanced Matching)
 * Dönüşüm API'si (CAPI) ile eşleşme oranını artırır
 */
export function setMetaUserData(userData: UserData) {
  try {
    if (window.fbq && pazarlamaIzniVar()) {
      const advancedMatchingData: Record<string, string> = {};
      if (userData.email) advancedMatchingData.em = userData.email.toLowerCase().trim();
      if (userData.phone) advancedMatchingData.ph = userData.phone.replace(/\D/g, '');
      if (userData.first_name) advancedMatchingData.fn = userData.first_name.toLowerCase().trim();
      if (userData.last_name) advancedMatchingData.ln = userData.last_name.toLowerCase().trim();
      
      // Re-init with advanced matching data
      window.fbq('init', '1155744453082317', advancedMatchingData);
    }
  } catch (e) {
    console.warn('[Analytics] Meta Advanced Matching failed:', e);
  }
}

/**
 * GA4 Enhanced Conversions - Kullanıcı verisini gönder
 * Google Ads dönüşüm takibi için gerekli
 */
export function setGA4UserData(userData: UserData) {
  try {
    // Enhanced conversions reklam verisidir (ad_user_data): yalnızca pazarlama izniyle
    if (window.gtag && pazarlamaIzniVar()) {
      const userDataForGA: Record<string, any> = {};
      if (userData.email) userDataForGA.email = userData.email.toLowerCase().trim();
      if (userData.phone) userDataForGA.phone_number = userData.phone;
      if (userData.first_name) userDataForGA.first_name = userData.first_name;
      if (userData.last_name) userDataForGA.last_name = userData.last_name;

      window.gtag('set', 'user_data', userDataForGA);
    }
  } catch (e) {
    console.warn('[Analytics] GA4 Enhanced Conversions user data failed:', e);
  }
}

/**
 * ChatGPT (OpenAI) Pixel event gönderimi
 */
export function trackOpenAILead(eventId?: string, eventName: string = "lead_created") {
  try {
    if (window.oaiq && pazarlamaIzniVar()) {
      // event_id, sunucu tarafı Dönüşümler API'siyle tekilleştirme (dedup) içindir:
      // aynı id iki kanaldan gelirse OpenAI tek dönüşüm sayar.
      if (eventId) {
        window.oaiq("measure", eventName, { type: "customer_action" }, { event_id: eventId });
      } else {
        window.oaiq("measure", eventName, { type: "customer_action" });
      }
    }
  } catch (e) {
    console.warn('[Analytics] OpenAI Pixel event failed:', e);
  }
}

/**
 * ChatGPT (OpenAI) Pixel — içerik görüntüleme dönüşümü (blog yazısı okunduğunda)
 */
export function trackOpenAIContentView() {
  try {
    if (window.oaiq && pazarlamaIzniVar()) {
      window.oaiq("measure", "contents_viewed", { type: "contents" });
    }
  } catch (e) {
    console.warn('[Analytics] OpenAI content view failed:', e);
  }
}

/**
 * ChatGPT (OpenAI) Pixel — sayfa görüntüleme dönüşümü (her rota değişiminde)
 */
export function trackOpenAIPageView() {
  try {
    if (window.oaiq && pazarlamaIzniVar()) {
      window.oaiq("measure", "page_viewed", { type: "contents" });
    }
  } catch (e) {
    console.warn('[Analytics] OpenAI page view failed:', e);
  }
}

/**
 * OpenAI atıf çerezlerini okur — sunucu tarafı Dönüşümler API'sine iletilir.
 * __oppref: reklam tıklaması referansı (event seviyesi), __obref: tarayıcı referansı (user seviyesi).
 * pazarlamaIzni: sunucu, OpenAI Dönüşümler API'sine yalnızca bu true ise event gönderir.
 */
export function getOpenAIRefs(): { oppref?: string; obref?: string; pazarlamaIzni: boolean } {
  const pazarlamaIzni = pazarlamaIzniVar();
  const refs: { oppref?: string; obref?: string; pazarlamaIzni: boolean } = { pazarlamaIzni };
  if (!pazarlamaIzni) return refs;
  try {
    for (const part of document.cookie.split(';')) {
      const [k, ...v] = part.trim().split('=');
      if (k === '__oppref') refs.oppref = decodeURIComponent(v.join('='));
      if (k === '__obref') refs.obref = decodeURIComponent(v.join('='));
    }
  } catch {
    // çerez okunamazsa atıf verisi olmadan devam
  }
  return refs;
}

/** Piksel + CAPI tekilleştirmesi için paylaşılan event id üretir */
export function newLeadEventId(): string {
  try {
    return `lead-${crypto.randomUUID()}`;
  } catch {
    return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Hem GA4 hem Meta Pixel'e event gönder
 */
export function trackEvent(event: EventName, params?: EventParams) {
  // GA4 Event Mapping
  const ga4Map: Record<EventName, string> = {
    generate_lead: 'generate_lead',
    schedule_meeting: 'schedule',
    request_callback: 'generate_lead',
    submit_feedback: 'submit_form',
    calculate_roi: 'view_item',
    page_view: 'page_view',
    contact: 'contact',
    begin_checkout: 'begin_checkout',
    view_item: 'view_item',
  };

  // Meta Pixel Event Mapping
  const metaMap: Record<EventName, string> = {
    generate_lead: 'Lead',
    schedule_meeting: 'Schedule',
    request_callback: 'Lead',
    submit_feedback: 'SubmitApplication',
    calculate_roi: 'ViewContent',
    page_view: 'PageView',
    contact: 'Contact',
    begin_checkout: 'InitiateCheckout',
    view_item: 'ViewContent',
  };

  trackGA4Event(ga4Map[event], {
    event_category: 'engagement',
    ...params,
  });

  trackMetaEvent(metaMap[event], params);
}

/**
 * 🔥 Dönüşüm Tracking - Lead formu, toplantı talebi, geri arama talebi
 * GA4 Enhanced Conversions + Meta Advanced Matching ile çalışır
 */
/** GA4 'lead_type' özel boyutunun değerleri */
export type LeadType = 'lead' | 'meeting' | 'callback' | 'analysis';

export function trackConversion(
  conversionType: LeadType | 'roi_calc',
  details?: Record<string, any>,
  userData?: UserData,
  openaiEventId?: string
) {
  // Kullanıcı verisi varsa gelişmiş eşleştirme yap
  if (userData) {
    setGA4UserData(userData);
    setMetaUserData(userData);
  }

  const conversionConfig = {
    lead: {
      ga4Event: 'generate_lead',
      metaEvent: 'Lead',
      ga4Value: 50,
    },
    meeting: {
      ga4Event: 'generate_lead',
      metaEvent: 'Schedule',
      ga4Value: 100,
    },
    callback: {
      ga4Event: 'generate_lead',
      metaEvent: 'Lead',
      ga4Value: 30,
    },
    analysis: {
      ga4Event: 'generate_lead',
      metaEvent: 'CompleteRegistration',
      ga4Value: 75,
    },
    roi_calc: {
      ga4Event: 'view_item',
      metaEvent: 'ViewContent',
      ga4Value: 10,
    },
  };

  const config = conversionConfig[conversionType];

  // ChatGPT (OpenAI) Pixel — lead niteliğindeki dönüşümlerde
  // (randevu ayrı dönüşüm etkinliği: appointment_scheduled)
  if (conversionType !== 'roi_calc') {
    trackOpenAILead(openaiEventId, conversionType === 'meeting' ? 'appointment_scheduled' : 'lead_created');
  }

  // GA4 Conversion with value
  // lead_type: dört lead türü aynı generate_lead olayında ayrışsın diye; GA4'te olay kapsamlı özel boyut
  // (Yönetici › Özel tanımlar › "lead_type"). details'tan sonra yazılır ki çağıran ezmesin; roi_calc lead değildir.
  // İzin kontrolü trackGA4Event içinde (analitik izni).
  trackGA4Event(config.ga4Event, {
    event_category: 'conversion',
    event_label: conversionType,
    value: config.ga4Value,
    currency: 'TRY',
    ...details,
    ...(conversionType !== 'roi_calc' ? { lead_type: conversionType } : {}),
  });

  // Meta Pixel Conversion
  trackMetaEvent(config.metaEvent, {
    content_name: conversionType,
    content_category: 'conversion',
    value: config.ga4Value,
    currency: 'TRY',
    ...details,
  });

  // DataLayer push for GTM / server-side tracking (kişisel veri dataLayer'a yazılmaz)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'dijipo_conversion',
      conversion_type: conversionType,
      conversion_value: config.ga4Value,
      ...details,
    });
  }
}

/**
 * Sayfa görüntüleme tracking (SPA route changes)
 */
export function trackPageView(path?: string) {
  const pagePath = path || window.location.pathname;

  trackGA4Event('page_view', {
    page_path: pagePath,
    page_title: document.title,
    page_location: window.location.href,
  });

  trackMetaEvent('PageView');
}
